import {HttpClient, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {getParams} from '../utils/utility-functions';
import {Pager} from '../models';

/**
 * Creates the Pager object which returns all the necessary items to create pagination.
 */
@Injectable()
export class PaginationService {
  constructor(private http: HttpClient) {
  }

  /**
   * Return the Pager object with all the needed fields.
   *
   * @param totalItems Number of all the items
   * @param currentPage Current page
   * @param pageSize Number of items per page
   * @returns Pager - totalItems, currentPage, pageSize, totalPages, startPage, endPage, startIndex, endIndex, pages
   */
  public getPager(totalItems: number, currentPage: number = 1, pageSize: number = 5): Pager {
    // calculate total pages
    const totalPages = Math.ceil(totalItems / pageSize);

    // ensure current page isn't out of range
    if (currentPage < 1) {
      currentPage = 1;
    } else if (currentPage > totalPages) {
      currentPage = totalPages;
    }

    const startPage = 1;
    const endPage = totalPages;

    // calculate start and end item indexes
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = Math.min(startIndex + pageSize - 1, totalItems - 1);

    const pages = this.calculatePages(currentPage, totalPages);

    const pager = {
      totalItems,
      currentPage,
      pageSize,
      totalPages,
      startPage,
      endPage,
      startIndex,
      endIndex,
      pages
    };

    // return object with all pager properties required by the view
    return new Pager(pager);
  }

  /**
   * Calculates the pages
   */
  private calculatePages(currentPage: number, lastPage: number): any {
    const last = lastPage;
    const delta = 2;
    const left = currentPage - delta;
    const right = currentPage + delta + 1;
    const range: Array<number> = [];
    const rangeWithDots: Array<number | string> = [];

    for (let i = 1; i <= last; i++) {
      if (i === 1 || i === last || (i >= left && i < right)) {
        range.push(i);
      }
    }

    let l: number = 0;
    for (const i of range) {
      if (l !== 0) {
        if (i - l === delta) {
          rangeWithDots.push(l + 1);
        } else if (i - l !== 1) {
          rangeWithDots.push('...');
        }
      }
      rangeWithDots.push(i);
      l = i;
    }

    return rangeWithDots;
  }

  /**
   * Fetch the data with pagination
   */
  public queryPaginated(baseUrl: string, queries?: Array<{ key: string; value: any }>): Observable<any> {
    let params = new HttpParams();
    const url = baseUrl;
    if (queries) {
      params = getParams(queries);
    }
    return this.http.get<any>(url, {params});
  }
}
